
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { BarChart, Globe, Video, PenTool, Palette, Users, Search } from 'lucide-react';

const CoreServices = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const services = [
    {
      icon: <BarChart size={36} className="text-blue-500" />,
      title: 'Digital Marketing',
      description: 'Strategic campaigns to boost your online presence and drive results',
      link: '/services/digital-marketing',
    },
    {
      icon: <Globe size={36} className="text-blue-500" />,
      title: 'Web & App Development',
      description: 'Custom websites and applications tailored to your business needs',
      link: '/services/web-development',
    },
    {
      icon: <Video size={36} className="text-blue-500" />,
      title: 'Video Ads & Editing',
      description: 'Compelling video content that engages your audience',
      link: '/services/video-ads',
    },
    {
      icon: <PenTool size={36} className="text-blue-500" />,
      title: 'Graphics & Poster Design',
      description: 'Eye-catching visual content that strengthens your brand',
      link: '/services/design',
    },
    {
      icon: <Palette size={36} className="text-blue-500" />,
      title: 'Logo & 3D Animation',
      description: 'Distinctive brand identity and animated content',
      link: '/services/animation',
    },
    {
      icon: <Users size={36} className="text-blue-500" />,
      title: 'Influencer Marketing',
      description: 'Connect with relevant influencers to amplify your message',
      link: '/services/influencer-marketing',
    },
    {
      icon: <Search size={36} className="text-blue-500" />,
      title: 'SEO Optimization',
      description: 'Improve your search rankings and drive organic traffic',
      link: '/services/seo',
    },
  ];

  return (
    <section className="py-20 bg-gray-50" id="services">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="section-heading"
          >
            Our Core <span className="text-blue-500">Services</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="section-subheading"
          >
            Comprehensive digital solutions to help your business grow and succeed
          </motion.p>
        </div>

        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8"
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              transition={{ duration: 0.4 }}
              className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300"
              whileHover={{ scale: 1.03 }}
            >
              <div className="mb-4 p-3 bg-blue-50 rounded-full inline-block">
                {service.icon}
              </div>
              <h3 className="text-xl font-bold mb-2">{service.title}</h3>
              <p className="text-gray-600 mb-4">{service.description}</p>
              <a 
                href={service.link} 
                className="text-blue-500 font-medium hover:text-blue-700 flex items-center"
              >
                Learn more
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-4 w-4 ml-1" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>
            </motion.div>
          ))}
        </motion.div>

        {inView && (
          <div className="mt-16 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <a 
                href="/services" 
                className="cta-button-secondary inline-block"
              >
                View All Services
              </a>
            </motion.div>
          </div>
        )}
      </div>
    </section>
  );
};

export default CoreServices;
